// web/src/components/ceremony/transport.ts — the cross-device e2e share transport
// for the unseal ceremony (M3.2). A holder's DECRYPTED Shamir share must travel
// end-to-end to the requester WITHOUT the server ever reading it (R11 / ADR-002 D2).
//
// ZERO-TRUST RULE: a holder decrypts THEIR OWN shard locally, then RE-ENCRYPTS the
// raw share to the REQUESTER'S EPHEMERAL public key (ECDH→HKDF→AES-GCM, the same
// WireShard suite as sealing) before it crosses any wire. The server only relays /
// stores that opaque blob — it holds no private key and can never open it.
//
// Three layers, tried/blended best-effort, all behind ONE `ShareTransport` the
// ceremony consumes unchanged:
//   1. PRIMARY  — encrypted store-and-forward relay over the API/DO. Holder POSTs
//                 the opaque blob to /quorum/:id/deliver; server announces a
//                 metadata-only 'shard.delivered'; requester drains the blobs from
//                 /quorum/:id/deliveries and decrypts each with its ephemeral key.
//                 THIS is what fixes cross-device ceremonies.
//   2. SECONDARY — WebRTC DataChannel peer-to-peer, signaled over the DO. SEAM ONLY
//                 in this slice (see webrtc.ts TODO); it layers on top without
//                 changing this interface and always falls back to (1).
//   3. FALLBACK  — same-origin BroadcastChannel, for same-browser multi-tab only.
//
// The plaintext share is decrypted ONLY in the requester's browser at quorum; the
// server's view is the ciphertext-free tally + the opaque relay blobs.
import {
  decryptShare,
  keyManager,
  shardEncryptor,
  type PrivateKeyHandle,
  type PublicKeyId,
  type PublicKeyJwk,
  type RawShare,
  type WireShard,
} from '../../crypto';
import { api } from '../../api';
import { RealtimeClient } from '../../ws';
import type { RealtimeEvent } from '../../../../shared/types';

/** A decrypted share in flight to the requester. CLIENT-SIDE ONLY — never server. */
export interface ShareEnvelope {
  requestId: string;
  /** Shamir x-coordinate (public bookkeeping; safe to carry alongside the share). */
  shareIndex: number;
  /** The decrypted Shamir share. Confidential; e2e to the requester only. */
  share: RawShare;
}

export interface ShareTransport {
  /** Holder side: deliver a decrypted share e2e to the requester. */
  send(env: ShareEnvelope): void | Promise<void>;
  /** Requester side: receive shares as holders submit; returns an unsubscribe. */
  onShare(handler: (env: ShareEnvelope) => void): () => void;
  close(): void;
}

/**
 * The requester's EPHEMERAL recipient key for a ceremony. Generated in-browser when
 * the requester convenes; the PUBLIC half is published on the unseal request, the
 * PRIVATE handle stays in memory and decrypts the relayed blobs. Distinct from the
 * holder's long-lived registry key — it exists only for this ceremony.
 */
export interface RequesterEphemeralKey {
  /** keyId derived from the public key — what holders seal the WireShard to. */
  keyId: PublicKeyId;
  /** The ephemeral public key (the JSON of this is published on the request). */
  publicKey: PublicKeyJwk;
  /** In-memory private handle (extractable:false). Decrypts the delivered blobs. */
  handle: PrivateKeyHandle;
}

/**
 * Generate the requester's ephemeral ECDH P-256 keypair for a ceremony (M3.2).
 * The private key is non-extractable and lives only in this tab's memory — it is
 * NEVER persisted or sent anywhere. Returns the keyId + public JWK to publish and
 * the private handle the transport uses to decrypt delivered blobs.
 */
export async function createRequesterEphemeralKey(): Promise<RequesterEphemeralKey> {
  const pair = (await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false, // private half non-extractable — parity with the long-lived custody (R11)
    ['deriveBits'],
  )) as CryptoKeyPair;
  const jwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
  const publicKey: PublicKeyJwk = {
    kty: 'EC',
    crv: 'P-256',
    x: jwk.x as string,
    y: jwk.y as string,
    ext: true,
    key_ops: [],
  };
  const keyId = await keyManager.deriveKeyId(publicKey);
  return { keyId, publicKey, handle: { keyId, cryptoKey: pair.privateKey } };
}

/** Options that shape the layered transport for one ceremony participant. */
export interface ShareTransportOptions {
  /**
   * Requester-only: the ephemeral private handle used to decrypt relayed blobs.
   * Present ⇒ this transport drains /deliveries and decrypts on 'shard.delivered'.
   */
  ephemeral?: RequesterEphemeralKey;
  /**
   * Holder-only: the recipient the decrypted share is re-encrypted TO (the
   * requester's ephemeral public key) + this holder's own member id, so `send`
   * can POST the opaque blob to the relay. Without it, `send` degrades to the
   * same-browser BroadcastChannel fallback only.
   */
  deliver?: {
    memberId: string;
    recipientKeyId: PublicKeyId;
    recipientPublicKey: PublicKeyJwk;
  };
}

/** Encode a WireShard as the opaque relay blob (the server treats it as bytes). */
function blobOfShard(shard: WireShard): string {
  return JSON.stringify(shard);
}

/** Parse a relay blob back to a WireShard (requester side, before decrypt). */
function shardOfBlob(blob: string): WireShard {
  return JSON.parse(blob) as WireShard;
}

/**
 * The cross-device transport. PRIMARY path is the encrypted store-and-forward
 * relay; a BroadcastChannel runs alongside as the same-browser fallback so a
 * multi-tab demo still works even if the relay is unreachable.
 */
class RelayShareTransport implements ShareTransport {
  private ch: BroadcastChannel | null = null;
  private rt: RealtimeClient | null = null;
  private offRt: (() => void) | null = null;
  private closed = false;
  // Requester: blobs already decrypted, so a re-pull on each event is idempotent.
  private seen = new Set<string>();

  constructor(
    private readonly requestId: string,
    private readonly opts: ShareTransportOptions = {},
  ) {
    if (typeof BroadcastChannel !== 'undefined') {
      this.ch = new BroadcastChannel(`bg-unseal-share-${requestId}`);
    }
  }

  /** Holder side: re-encrypt the raw share to the requester ephemeral key, deliver. */
  async send(env: ShareEnvelope): Promise<void> {
    // Same-browser fallback: a sibling tab acting as requester gets it instantly.
    this.ch?.postMessage(env);

    const deliver = this.opts.deliver;
    if (!deliver) return; // no relay target (e.g. legacy ceremony) — fallback only.

    // ZERO-TRUST: encrypt the RAW share to the requester's EPHEMERAL public key
    // here, on the holder's device. Only the opaque WireShard leaves the browser.
    const shard = await shardEncryptor.encryptShare(env.share, env.shareIndex, {
      keyId: deliver.recipientKeyId,
      publicKey: deliver.recipientPublicKey,
    });
    await api.deliverShard(this.requestId, {
      memberId: deliver.memberId,
      shareIndex: env.shareIndex,
      blob: blobOfShard(shard),
    });
  }

  /** Requester side: drain relayed blobs (now + on each 'shard.delivered'). */
  onShare(handler: (env: ShareEnvelope) => void): () => void {
    // (a) Same-browser fallback channel.
    const ch = this.ch;
    const chListener = (e: MessageEvent): void => handler(e.data as ShareEnvelope);
    if (ch) ch.addEventListener('message', chListener);

    // (b) Relay path — only meaningful for the requester (needs the ephemeral key
    // to decrypt). Drain immediately for any blobs already waiting, then on each
    // 'shard.delivered' signal pull + decrypt the new ones.
    const ephemeral = this.opts.ephemeral;
    if (ephemeral) {
      void this.drain(ephemeral, handler);
      const rt = new RealtimeClient();
      this.rt = rt;
      this.offRt = rt.on((ev: RealtimeEvent) => {
        if (ev.type === 'shard.delivered' && ev.delivery.requestId === this.requestId) {
          void this.drain(ephemeral, handler);
        }
      });
      rt.connect();
    }

    return () => {
      if (ch) ch.removeEventListener('message', chListener);
      this.offRt?.();
      this.offRt = null;
      this.rt?.close();
      this.rt = null;
    };
  }

  /** Pull every opaque blob for this ceremony, decrypt the new ones in-browser. */
  private async drain(
    ephemeral: RequesterEphemeralKey,
    handler: (env: ShareEnvelope) => void,
  ): Promise<void> {
    if (this.closed) return;
    let deliveries;
    try {
      ({ deliveries } = await api.unsealDeliveries(this.requestId));
    } catch {
      return; // relay unreachable / not yet authorized — the fallback may still cover it.
    }
    for (const d of deliveries) {
      if (this.seen.has(d.memberId)) continue;
      try {
        const shard = shardOfBlob(d.blob);
        // Decrypt with the EPHEMERAL private handle — plaintext share never leaves here.
        const share = await decryptShare(shard, ephemeral.handle);
        this.seen.add(d.memberId);
        handler({ requestId: this.requestId, shareIndex: d.shareIndex, share });
      } catch {
        // A malformed/forged blob can't open under our key — skip it (the GCM tag
        // is the gate). The ceremony stays uncompromised; other blobs still apply.
      }
    }
  }

  close(): void {
    this.closed = true;
    this.offRt?.();
    this.offRt = null;
    this.rt?.close();
    this.rt = null;
    this.ch?.close();
    this.ch = null;
  }
}

/**
 * Create the ceremony's share transport. Pass the requester's `ephemeral` key to
 * drain+decrypt relayed blobs; pass `deliver` (holder) to re-encrypt+POST. With
 * neither it degrades to the same-browser BroadcastChannel fallback (last resort).
 */
export function createShareTransport(
  requestId: string,
  opts: ShareTransportOptions = {},
): ShareTransport {
  return new RelayShareTransport(requestId, opts);
}
